import React, { useState } from 'react';
import { useLoaderData } from 'react-router';
import { Button, Card, Stack, Typography } from '@mui/joy';
import { Round, RoundStatus } from '../models/round';
import HoleList from './HoleList';
import { serviceFacade } from '../services/service-facade';
import { Score } from '../models/player';

const RoundComponent = () => {
    const { round } = useLoaderData();
    const [currentRound, setCurrentRound] = useState<Round>(round);
    const [currentHole, setCurrentHole] = useState<number>(round.player.scores.length + 1);
    const [currentScore, setCurrentScore] = useState<number>(0);
    const [isLoading, setIsLoading] = useState(false);

    const handlePlusOne = () => {
        setCurrentScore(prev => prev + 1);
    };

    const handleMinusOne = () => {
        setCurrentScore(prev => (Math.max(prev - 1, 0)));
    };

    const handleHoleNavigation = async (offset: number) => {
        try {
            setIsLoading(true);
            let updatedRound = currentRound;
            if (currentScore !== 0) {
                updatedRound = await serviceFacade.updateScore(currentRound.id, currentRound.player!.id, currentHole, currentScore);
                setCurrentRound(updatedRound);
            }
            const nextHole = currentHole + offset;
            setCurrentHole(nextHole);
            const nextHoleScore = updatedRound.player!.scores.find((s: Score) => s.hole === nextHole);
            setCurrentScore(nextHoleScore ? nextHoleScore.score : 0);
        } catch (error) {
            console.error('Error updating round:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handlePreviousHole = async () => {
        if (currentHole <= 1) return;
        await handleHoleNavigation(-1);
    };

    const handleNextHole = async () => {
        await handleHoleNavigation(1);
    };

    const handleCompleteRound = async () => {
        try {
            setIsLoading(true);
            const updatedRound = await serviceFacade.completeRound(currentRound.id, currentRound.player!.id);
            setCurrentRound(updatedRound);
        } catch (error) {
            console.error('Error completing round:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleEditRound = async () => {
        try {
            setIsLoading(true);
            const updatedRound = await serviceFacade.editRound(currentRound.id, currentRound.player!.id);
            setCurrentRound(updatedRound);
        } catch (error) {
            console.error('Error editing round:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const isReadOnly = () => {
        return currentRound?.player?.status === RoundStatus.COMPLETED;
    };

    return (
        <Stack spacing={3}>
            <Card variant="outlined" className="round-card">
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography level="h3">{currentRound.name}</Typography>
                    <Typography
                        level="title-md">Player: {currentRound.player!.name}</Typography>
                </Stack>
            </Card>

            {!isReadOnly() && (<Card variant="soft" color="primary" className="score-card">
                <Stack spacing={2} alignItems="center">

                    <Stack direction="row" spacing={2} width="100%" justifyContent="space-between">
                        <Button
                            size="lg"
                            variant="outlined"
                            color="primary"
                            onClick={handleMinusOne}
                            fullWidth={true}
                            disabled={currentScore === 0}
                        >
                            -1
                        </Button>
                        <Stack spacing={2} whiteSpace={'nowrap'} alignItems="center">
                            <Typography level="h4">Hole {currentHole}</Typography>
                            <Typography level="h1"
                                        className="score-display">{currentScore}</Typography>
                        </Stack>
                        <Button
                            size="lg"
                            variant="solid"
                            color="primary"
                            fullWidth={true}
                            onClick={handlePlusOne}
                        >
                            +1
                        </Button>
                    </Stack>
                    <Stack direction="row" spacing={2} width="100%" justifyContent="space-between">
                        <Button
                            size="lg"
                            variant="outlined"
                            color="neutral"
                            fullWidth={true}
                            onClick={handlePreviousHole}
                            disabled={currentHole <= 1 || isLoading}
                            loading={isLoading}
                        >
                            Previous Hole
                        </Button>

                        <Button
                            size="lg"
                            variant="outlined"
                            color="neutral"
                            fullWidth={true}
                            onClick={handleNextHole}
                            disabled={currentScore === 0 || isLoading}
                            loading={isLoading}
                        >
                            Next Hole
                        </Button>
                    </Stack>
                </Stack>
            </Card>)}

            <HoleList currentRound={currentRound} />

            {isReadOnly() ? (
                <Button size="lg" onClick={handleEditRound} loading={isLoading}
                        disabled={isLoading}>Edit Round</Button>
            ) : (
                <Button size="lg" onClick={handleCompleteRound} loading={isLoading}
                        disabled={isLoading}>Finish Round</Button>
            )}

        </Stack>
    );
};

export default RoundComponent;
